import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Divider,
  Grid,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ContentCopy as DuplicateIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import MemoryGame from '../MemoryGame';
import Card from './Card';
import Button from './Button';
import TextField from './TextField';
import { useTranslation } from 'react-i18next';

// Компонент для красивого отображения JSON с контрастными цветами
const JsonPreview = ({ data }) => {
  const formatJSON = (obj) => {
    return JSON.stringify(obj, null, 2);
  };

  return (
    <pre
      style={{
        backgroundColor: 'background.default',
        padding: '15px',
        borderRadius: '4px',
        overflowX: 'auto',
        color: 'text.primary',
        fontFamily: 'monospace',
        fontSize: '14px',
        lineHeight: '1.4',
        margin: '10px 0',
      }}
    >
      {formatJSON(data)}
    </pre>
  );
};

/**
 * Компонент для визуального редактирования карточек Memory Game
 * @param {string} content - JSON строка с содержимым карточек
 * @param {function} onChange - Функция обработки изменений
 */
const MemoryGameEditor = ({ content, onChange }) => {
  const { t } = useTranslation(['lessons', 'common']);
  // Начальное состояние карточек
  const [cards, setCards] = useState([]);
  // Состояние для предпросмотра
  const [previewMode, setPreviewMode] = useState(false);
  // Следующий доступный pairId
  const [nextPairId, setNextPairId] = useState(1);

  // Отслеживаем, изменились ли карточки с момента последнего сохранения
  const [isDirty, setIsDirty] = useState(false);

  // Таймер для отложенного сохранения
  const saveTimeoutRef = useRef(null);

  // Загрузка карточек из JSON строки при инициализации или обновлении content
  useEffect(() => {
    try {
      const parsedCards = JSON.parse(content);
      if (Array.isArray(parsedCards)) {
        setCards(parsedCards);

        // Найдем максимальный pairId для установки следующего доступного
        const maxPairId = parsedCards.reduce((max, card) => {
          return Math.max(max, parseInt(card.pairId) || 0);
        }, 0);
        setNextPairId(maxPairId + 1);

        // Сбрасываем флаг изменений при загрузке новых данных
        setIsDirty(false);
      } else {
        // Если не массив, создаем пустой набор карточек
        setCards([]);
        setNextPairId(1);
      }
    } catch (error) {
      console.error(t('lessons:editor.memory.parseError'), error);
      // В случае ошибки, создаем пустой набор карточек
      setCards([]);
      setNextPairId(1);
    }
  }, [content, t]);

    // Непосредственное сохранение в родительский компонент
  const saveChangesToParent = useCallback(() => {
    if (isDirty) {
      onChange(JSON.stringify(cards, null, 2));
      setIsDirty(false);
    }
  }, [cards, isDirty, onChange]);

  // Эффект для отложенного сохранения с debounce
  useEffect(() => {
    if (isDirty) {
      // Очищаем предыдущий таймер
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Устанавливаем новый таймер для сохранения через 1 секунду
      saveTimeoutRef.current = setTimeout(() => {
        saveChangesToParent();
      }, 1000);
    }

    // Очистка при размонтировании
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [cards, isDirty, saveChangesToParent]);

  // Обновление карточек в локальном state без немедленного сохранения
  const updateCards = (newCards) => {
    setCards(newCards);
    setIsDirty(true);
  };

  // Добавление новой пары карточек
  const handleAddCardPair = () => {
    const newCards = [
      ...cards,
      { text: t('lessons:editor.memory.newCard'), pairId: nextPairId },
      { text: t('lessons:editor.memory.newPair'), pairId: nextPairId },
    ];

    updateCards(newCards);
    setNextPairId(nextPairId + 1);
  };

  // Удаление карточки
  const handleDeleteCard = (index) => {
    const cardToDelete = cards[index];
    const pairId = cardToDelete.pairId;

    // Находим все карточки с таким же pairId
    const pairedCards = cards.filter(
      (card, idx) => card.pairId === pairId && idx !== index
    );

    if (pairedCards.length === 1) {
      // Если удаляем одну из пары карточек, спросим пользователя что делать с парой
      if (window.confirm(t('lessons:editor.memory.deleteConfirm'))) {
        // Удаляем обе карточки
        const newCards = cards.filter((card) => card.pairId !== pairId);
        updateCards(newCards);
      } else {
        // Удаляем только выбранную карточку
        const newCards = [...cards];
        newCards.splice(index, 1);
        updateCards(newCards);
      }
    } else {
      // Если нет пары или несколько карточек с таким pairId
      const newCards = [...cards];
      newCards.splice(index, 1);
      updateCards(newCards);
    }
  };

  // Обновление текста карточки
  const handleCardTextChange = (index, newText) => {
    const newCards = [...cards];
    newCards[index].text = newText;
    updateCards(newCards);
  };

  // Обновление pairId карточки
  const handleCardPairIdChange = (index, newPairId) => {
    const newCards = [...cards];
    newCards[index].pairId = newPairId;
    updateCards(newCards);

    // Обновляем nextPairId если добавлен больший pairId
    const intPairId = parseInt(newPairId);
    if (!isNaN(intPairId) && intPairId >= nextPairId) {
      setNextPairId(intPairId + 1);
    }
  };

  // Дублирование пары карточек
  const handleDuplicatePair = (pairId) => {
    const pairCards = cards.filter((card) => card.pairId === pairId);
    if (pairCards.length < 1) return;

    const duplicatedCards = pairCards.map((card) => ({
      text: card.text,
      pairId: nextPairId,
    }));

    const newCards = [...cards, ...duplicatedCards];
    updateCards(newCards);
    setNextPairId(nextPairId + 1);
  };

  // Проверка валидности данных карточек
  const validateCards = () => {
    // Карточки по парам
    const pairCounts = {};
    cards.forEach((card) => {
      pairCounts[card.pairId] = (pairCounts[card.pairId] || 0) + 1;
    });

    // Проверка на наличие пар с неправильным количеством карточек
    const invalidPairs = Object.entries(pairCounts)
      .filter(([_, count]) => count !== 2)
      .map(([pairId, count]) => ({ pairId, count }));

    // Проверка на наличие пустых текстов
    // Защита от undefined: если card.text не строка, считаем как пустой
    const emptyTextCards = cards.filter(
      (card) => !(card.text || '').trim()
    ).length;

    return {
      isValid: invalidPairs.length === 0 && emptyTextCards === 0,
      invalidPairs,
      emptyTextCards,
    };
  };

  const validation = validateCards();

  // Группировка карточек по парам для более удобного отображения
  const groupedCards = {};
  cards.forEach((card, index) => {
    if (!groupedCards[card.pairId]) {
      groupedCards[card.pairId] = [];
    }
    groupedCards[card.pairId].push({ ...card, index });
  });

  // Если выбран режим предпросмотра, показываем игру Memory Game
  if (previewMode) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">{t('lessons:editor.memory.preview')}</Typography>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => setPreviewMode(false)}
              size="small"
            >
              {t('lessons:editor.exitPreview')}
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {cards.length > 0 ? (
            <>
              <MemoryGame
                options={cards}
                onChange={() => {}}
              />
            </>
          ) : (
            <Typography>{t('lessons:editor.memory.noCards')}</Typography>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2, minHeight: '200px', position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">{t('lessons:editor.memory.title')}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => setPreviewMode(!previewMode)}
              size="small"
            >
              {t('lessons:editor.preview')}
            </Button>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddCardPair}
              variant="contained"
              color="primary"
            >
              {t('lessons:editor.memory.newPair')}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            <InfoIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('lessons:editor.memory.formatInfo')}
          </Typography>
          <Typography variant="body2">
            {t('lessons:editor.memory.gameDescription')}
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {t('lessons:editor.memory.dataFormat')}:
          </Typography>
          <JsonPreview
            data={[
              { text: 'JavaScript', pairId: 1 },
              { text: 'ECMAScript', pairId: 1 },
              { text: 'HTML', pairId: 2 },
              { text: 'Markup Language', pairId: 2 },
            ]}
          />
        </Paper>

        {!validation.isValid && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div">
              {t('lessons:editor.memory.validationProblems')}:
              {validation.invalidPairs.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 1 }}>
                  {validation.invalidPairs.map(({ pairId, count }) => (
                    <li key={pairId}>
                      {t('lessons:editor.memory.invalidPairCount', { pairId, count })}
                    </li>
                  ))}
                </Box>
              )}
              {validation.emptyTextCards > 0 && (
                <Box component="li" sx={{ mt: 1 }}>
                  {t('lessons:editor.memory.emptyTextCards', {
                    count: validation.emptyTextCards,
                  })}
                </Box>
              )}
            </Typography>
          </Alert>
        )}

        {/* Отображаем карточки по парам */}
        {Object.entries(groupedCards).map(([pairId, pairCards]) => (
          <Paper key={pairId} elevation={0} sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="subtitle1">
                {t('lessons:editor.memory.pairNumber', {
                  number: pairId,
                  count: pairCards.length,
                })}
              </Typography>
              <Box>
                <Tooltip title={t('lessons:editor.memory.duplicatePair')}>
                  <IconButton onClick={() => handleDuplicatePair(pairId)}>
                    <DuplicateIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {pairCards.map((card) => (
                <Grid item xs={12} sm={6} key={card.index}>
                  <Card>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="subtitle2">
                        {t('lessons:editor.memory.cardNumber', {
                          number: card.index + 1,
                        })}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCard(card.index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <TextField
                      fullWidth
                      label={t('lessons:editor.memory.cardText')}
                      value={card.text}
                      onChange={(e) =>
                        handleCardTextChange(card.index, e.target.value)
                      }
                      margin="normal"
                    />

                    <TextField
                      label={t('lessons:editor.memory.pairId')}
                      value={card.pairId}
                      onChange={(e) =>
                        handleCardPairIdChange(card.index, e.target.value)
                      }
                      size="small"
                      margin="normal"
                      sx={{ width: '100px' }}
                    />
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))}

        {cards.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              mb: 3,
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t('lessons:editor.memory.emptyStateMessage')}
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddCardPair}
              variant="contained"
              color="primary"
              sx={{ mt: 1 }}
            >
              {t('lessons:editor.memory.newPair')}
            </Button>
          </Box>
        )}

        {cards.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddCardPair}
              variant="contained"
              color="primary"
            >
              {t('lessons:editor.memory.newPair')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MemoryGameEditor;
